import { Switch, Route, useLocation } from "wouter";
import { Suspense, lazy, useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/use-auth";
import { ErrorBoundary } from "@/components/error-boundary";
import { LabScientistProvider, LabScientistContext } from "@/hooks/use-lab-scientist";
import { ScientistBadge } from "@/components/scientist-badge";
import React from 'react';
import { PageLoader } from "@/components/page-loader";
import { ThemeProvider } from "@/components/theme-provider";
import { useI18n } from "@/lib/i18n";
import { WebVitalsMonitor } from "@/hooks/use-web-vitals";
import { UpdateNotification } from "@/components/UpdateNotification";
import { SkipLink } from "@/components/skip-link";
import { PWAInstallPrompt } from "@/components/pwa-install-prompt";

import NotFound from "@/pages/not-found";

// Home is lazy loaded but preloaded for fast initial paint
const Home = lazy(() => import("@/pages/home"));
const ForBrands = lazy(() => import("@/pages/for-brands"));

// Preload Home immediately for fast LCP
if (typeof window !== 'undefined') {
  import("@/pages/home");
}
const ForCreators = lazy(() => import("@/pages/for-creators"));
const Academy = lazy(() => import("@/pages/academy"));
const CreatorsList = lazy(() => import("@/pages/creators-list"));
const CreatorProfile = lazy(() => import("@/pages/creator-profile"));
const Podcast = lazy(() => import("@/pages/podcast"));
const Events = lazy(() => import("@/pages/events"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Asesorias = lazy(() => import("@/pages/asesorias"));
const CheckoutSuccess = lazy(() => import("@/pages/checkout-success"));
const CheckoutCancel = lazy(() => import("@/pages/checkout-cancel"));
const DescargarPlantilla = lazy(() => import("@/pages/descargar-plantilla"));
const Portfolio = lazy(() => import("@/pages/portfolio"));
const UGC = lazy(() => import("@/pages/ugc"));
const Login = lazy(() => import("@/pages/login"));

const AdminDashboard = lazy(() => import("@/pages/admin/dashboard"));
const AdminMessages = lazy(() => import("@/pages/admin/messages"));
const CreatorDashboard = lazy(() => import("@/pages/creator/dashboard"));
const CreatorMessages = lazy(() => import("@/pages/creator/messages"));
const CRMClientes = lazy(() => import("@/pages/admin/crm/clientes"));
const CRMLeads = lazy(() => import("@/pages/admin/crm/leads"));
const CRMTrabajos = lazy(() => import("@/pages/admin/crm/trabajos"));
const CRMFacturas = lazy(() => import("@/pages/admin/crm/facturas"));
const CRMGastos = lazy(() => import("@/pages/admin/crm/gastos"));
const AdminEventos = lazy(() => import("@/pages/admin/eventos"));
const AdminCursos = lazy(() => import("@/pages/admin/cursos"));
const AdminPodcast = lazy(() => import("@/pages/admin/podcast"));
const AdminPortfolio = lazy(() => import("@/pages/admin/portfolio"));
const AdminCreadores = lazy(() => import("@/pages/admin/creadores"));
const AdminCreatorApplications = lazy(() => import("@/pages/admin/creator-applications"));
const AdminAsesoriaLeads = lazy(() => import("@/pages/admin/asesoria-leads"));
const AdminPortfolioContacts = lazy(() => import("@/pages/admin/portfolio-contacts"));
const AdminContacts = lazy(() => import("@/pages/admin/contacts"));
const AdminTestimonios = lazy(() => import("@/pages/admin/testimonios"));
const AdminAnalytics = lazy(() => import("@/pages/admin/analytics"));
const AdminUsuarios = lazy(() => import("@/pages/admin/usuarios"));
const AdminKanban = lazy(() => import("@/pages/admin/kanban"));
const AdminFiscalReports = lazy(() => import("@/pages/admin/fiscal-reports"));
const AdminActivityLog = lazy(() => import("@/pages/admin/activity-log"));
const AdminCreatorPerformance = lazy(() => import("@/pages/admin/creator-performance"));
const AdminNotificationsCenter = lazy(() => import("@/pages/admin/notifications-center"));
const AdminBanners = lazy(() => import("@/pages/admin/banners"));
const AdminFeaturesPDF = lazy(() => import("@/pages/admin/features-pdf"));
const AdminBlog = lazy(() => import("@/pages/admin/blog"));
const AdminCupones = lazy(() => import("@/pages/admin/cupones"));
const AdminAfiliados = lazy(() => import("@/pages/admin/afiliados"));
const AdminWebhooks = lazy(() => import("@/pages/admin/webhooks"));
const AdminConfiguracion = lazy(() => import("@/pages/admin/configuracion"));
const AdminNotificacionesMasivas = lazy(() => import("@/pages/admin/notificaciones-masivas"));
const AdminCampaigns = lazy(() => import("@/pages/admin/campaigns"));
const AdminSalesDashboard = lazy(() => import("@/pages/admin/sales-dashboard"));
const AdminDownloadLogs = lazy(() => import("@/pages/admin/download-logs"));
const AdminNotionAccess = lazy(() => import("@/pages/admin/notion-access"));
const AdminAiVideos = lazy(() => import("@/pages/admin/ai-videos"));
const AdminSecretLab = lazy(() => import("@/pages/admin/secret-lab"));
const AdminProducts = lazy(() => import("@/pages/admin/products"));
const CreatorPortfolioEditor = lazy(() => import("@/pages/admin/creator-portfolio-editor"));
const EventDetail = lazy(() => import("@/pages/event-detail"));
const PortfolioDetail = lazy(() => import("@/pages/portfolio-detail"));
const Blog = lazy(() => import("@/pages/blog"));
const BlogPost = lazy(() => import("@/pages/blog-post"));
const CourseDetail = lazy(() => import("@/pages/course-detail"));
const Comparativa = lazy(() => import("@/pages/comparativa"));

const UriPortfolio = lazy(() => import("@/pages/uri-portfolio"));
const PresentacionPloom = lazy(() => import("@/pages/presentacion-ploom"));
const PresentacionMedisana = lazy(() => import("@/pages/presentacion-medisana"));
const PresentacionBusquedaReporteroMadrid = lazy(
  () => import("@/pages/presentacion-busqueda-reportero-madrid")
);
const StudentLogin = lazy(() => import("@/pages/student/login"));
const StudentDashboard = lazy(() => import("@/pages/student/dashboard"));
const StudentCourse = lazy(() => import("@/pages/student/course"));
const StudentLesson = lazy(() => import("@/pages/student/lesson"));
const VerifyCertificate = lazy(() => import("@/pages/verify-certificate"));
const MiPerfil = lazy(() => import("@/pages/mi-perfil"));
const PurchaseHistory = lazy(() => import("@/pages/user/purchase-history"));

const ClientDashboard = lazy(() => import("@/pages/client/dashboard"));
const ClientCampaigns = lazy(() => import("@/pages/client/campaigns"));
const ClientNewCampaign = lazy(() => import("@/pages/client/new-campaign"));
const ClientInvoices = lazy(() => import("@/pages/client/invoices"));

const AvisoLegal = lazy(() => import("@/pages/legal").then(m => ({ default: m.AvisoLegal })));
const Privacidad = lazy(() => import("@/pages/legal").then(m => ({ default: m.Privacidad })));
const PoliticaCookies = lazy(() => import("@/pages/legal").then(m => ({ default: m.Cookies })));
const Terminos = lazy(() => import("@/pages/legal").then(m => ({ default: m.Terminos })));
const TerminosProductosDigitales = lazy(() => import("@/pages/terminos-productos-digitales"));

function ScrollToTop() {
  const [location] = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  
  return null;
}

function LanguageInitializer() {
  const initialize = useI18n((state) => state.initialize);
  
  useEffect(() => {
    initialize();
  }, [initialize]);
  
  return null;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/marcas" component={ForBrands} />
          <Route path="/para-creadores" component={ForCreators} />
          <Route path="/asesorias" component={Asesorias} />
          <Route path="/checkout/success" component={CheckoutSuccess} />
          <Route path="/checkout/cancel" component={CheckoutCancel} />
          <Route path="/descargar-plantilla" component={DescargarPlantilla} />
          <Route path="/portfolio" component={Portfolio} />
          <Route path="/portfolio/:idOrSlug" component={PortfolioDetail} />
          <Route path="/blog" component={Blog} />
          <Route path="/blog/:slug" component={BlogPost} />
          <Route path="/comparativa" component={Comparativa} />
          <Route path="/cursos/:slug" component={CourseDetail} />
          <Route path="/ugc" component={UGC} />
          <Route path="/academia-ugc" component={Academy} />
          <Route path="/creadores" component={CreatorsList} />
          <Route path="/podcast" component={Podcast} />
          <Route path="/eventos" component={Events} />
          <Route path="/eventos/:slug" component={EventDetail} />
          <Route path="/sobre-nosotros" component={About} />
          <Route path="/contacto" component={Contact} />
          <Route path="/urits" component={UriPortfolio} />

          {/* Presentation pages for agencies */}
          <Route path="/presentacion/ploom" component={PresentacionPloom} />
          <Route path="/presentacion/medisana" component={PresentacionMedisana} />
          <Route
            path="/presentacion/busqueda-reportero-madrid"
            component={PresentacionBusquedaReporteroMadrid}
          />
          
          {/* Legal Pages */}
          <Route path="/aviso-legal" component={AvisoLegal} />
          <Route path="/privacidad" component={Privacidad} />
          <Route path="/cookies" component={PoliticaCookies} />
          <Route path="/terminos" component={Terminos} />
          <Route path="/terminos-productos-digitales" component={TerminosProductosDigitales} />
          
          {/* User Profile (all roles) */}
          <Route path="/mi-perfil" component={MiPerfil} />
          <Route path="/mis-compras" component={PurchaseHistory} />

          {/* Admin Login & Dashboard */}
          <Route path="/admin/login" component={Login} />
          <Route path="/admin" component={AdminDashboard} />
          <Route path="/admin/dashboard" component={AdminDashboard} />

          {/* Admin CRM */}
          <Route path="/admin/crm/clientes" component={CRMClientes} />
          <Route path="/admin/crm/leads" component={CRMLeads} />
          <Route path="/admin/crm/trabajos" component={CRMTrabajos} />
          <Route path="/admin/crm/facturas" component={CRMFacturas} />
          <Route path="/admin/crm/gastos" component={CRMGastos} />
          
          {/* Admin Content */}
          <Route path="/admin/eventos" component={AdminEventos} />
          <Route path="/admin/cursos" component={AdminCursos} />
          <Route path="/admin/podcast" component={AdminPodcast} />
          <Route path="/admin/portfolio" component={AdminPortfolio} />
          <Route path="/admin/creadores" component={AdminCreadores} />
          <Route path="/admin/creator-applications" component={AdminCreatorApplications} />
          <Route path="/admin/asesoria-leads" component={AdminAsesoriaLeads} />
          <Route path="/admin/portfolio-contacts" component={AdminPortfolioContacts} />
          <Route path="/admin/contacts" component={AdminContacts} />
          <Route path="/admin/creator-portfolio/:id" component={CreatorPortfolioEditor} />
          <Route path="/admin/testimonios" component={AdminTestimonios} />
          
          {/* Admin Messages & Analytics */}
          <Route path="/admin/messages" component={AdminMessages} />
          <Route path="/admin/analytics" component={AdminAnalytics} />
          <Route path="/admin/usuarios" component={AdminUsuarios} />
          
          {/* Admin Advanced Features */}
          <Route path="/admin/kanban" component={AdminKanban} />
          <Route path="/admin/fiscal" component={AdminFiscalReports} />
          <Route path="/admin/activity-log" component={AdminActivityLog} />
          <Route path="/admin/creator-performance" component={AdminCreatorPerformance} />
          <Route path="/admin/notifications" component={AdminNotificationsCenter} />
          <Route path="/admin/banners" component={AdminBanners} />
          <Route path="/admin/features-pdf" component={AdminFeaturesPDF} />
          
          {/* Admin Web Management */}
          <Route path="/admin/blog" component={AdminBlog} />
          <Route path="/admin/cupones" component={AdminCupones} />
          <Route path="/admin/afiliados" component={AdminAfiliados} />
          <Route path="/admin/webhooks" component={AdminWebhooks} />
          <Route path="/admin/configuracion" component={AdminConfiguracion} />
          <Route path="/admin/notificaciones-masivas" component={AdminNotificacionesMasivas} />
          <Route path="/admin/campaigns" component={AdminCampaigns} />
          <Route path="/admin/ventas" component={AdminSalesDashboard} />
          <Route path="/admin/download-logs" component={AdminDownloadLogs} />
          <Route path="/admin/notion-access" component={AdminNotionAccess} />
          <Route path="/admin/ai-videos" component={AdminAiVideos} />
          <Route path="/admin/secret-lab" component={AdminSecretLab} />
          <Route path="/admin/productos" component={AdminProducts} />

          {/* Admin generic section (must come AFTER specific routes) */}
          <Route path="/admin/:section" component={AdminDashboard} />

          {/* Creator Dashboard (specific routes first) */}
          <Route path="/creator" component={CreatorDashboard} />
          <Route path="/creator/messages" component={CreatorMessages} />
          <Route path="/creator/perfil" component={CreatorDashboard} />
          <Route path="/creator/campanas" component={CreatorDashboard} />
          <Route path="/creator/pagos" component={CreatorDashboard} />
          <Route path="/creator/academia" component={CreatorDashboard} />
          
          {/* Public Creator Profile (after dashboard routes to avoid conflict) */}
          <Route path="/creator/:slug" component={CreatorProfile} />

          {/* Student Area */}
          <Route path="/student/login" component={StudentLogin} />
          <Route path="/student/dashboard" component={StudentDashboard} />
          <Route path="/student/course/:courseId" component={StudentCourse} />
          <Route path="/student/lesson/:lessonId" component={StudentLesson} />

          {/* Certificate Verification */}
          <Route path="/verificar" component={VerifyCertificate} />
          <Route path="/verificar/:certificateNumber" component={VerifyCertificate} />

          {/* Brand Portal (Client Dashboard) */}
          <Route path="/marca" component={ClientDashboard} />
          <Route path="/marca/campanas" component={ClientCampaigns} />
          <Route path="/marca/nueva-campana" component={ClientNewCampaign} />
          <Route path="/marca/facturacion" component={ClientInvoices} />
          <Route path="/marca/analytics" component={ClientDashboard} />
          <Route path="/marca/configuracion" component={ClientDashboard} />

          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function ClickTracker({ children }: { children: React.ReactNode }) {
  const { incrementClick } = useLabScientistSafe();
  
  useEffect(() => {
    const handleClick = () => incrementClick();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [incrementClick]);
  
  return <>{children}</>;
}

function useLabScientistSafe() {
  const context = React.useContext(LabScientistContext);
  if (!context) {
    return { incrementClick: () => {} };
  }
  return context;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <LabScientistProvider>
              <TooltipProvider>
                <ClickTracker>
                  <SkipLink />
                  <PWAInstallPrompt />
                  <LanguageInitializer />
                  <WebVitalsMonitor />
                  <Toaster />
                  <SonnerToaster position="top-center" richColors />
                  <main id="main-content">
                    <Router />
                  </main>
                  <UpdateNotification />
                  <ScientistBadge />
                </ClickTracker>
              </TooltipProvider>
            </LabScientistProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
